import { formatBillingPeriod } from "@/app/api/apiHelpers";
import { formatPrice } from "@/app/(core)/profile/utils";
import { SelectedSubscriptionMeta } from "@/app/(core)/register/types";
import { SubscriptionMeta } from "@/types/next-auth";
import {
  CustomStripePrice,
  CustomStripeProduct,
  StripeProductMetadata,
} from "@/types/product-schema";
import { Subject } from "@/types/subject-schema";
import Stripe from "stripe";
import { isoStringToUnix } from "../stripe/formatters";

/**
 * Returns the number of days until the next billing date.
 * @param billingAnchor - The billing anchor string (e.g. "2026-01-20")
 * @returns The number of days until the next billing date
 */
export function getDaysUntilNextBill(billingAnchor: string) {
  const unixBillingAnchor = isoStringToUnix(billingAnchor);
  const unixDeltaToBillingAnchor = unixBillingAnchor - Date.now();
  const daysUntilNextBill =
    unixDeltaToBillingAnchor > 0
      ? Math.floor(unixDeltaToBillingAnchor / (24 * 60 * 60 * 1000))
      : 0;
  return daysUntilNextBill;
}

/**
 * Returns the suffix for a price period.
 * @param billingPeriod - The billing period string (e.g. "month-1", "year-1")
 * @returns The suffix for the price period
 */
export function getPricePeriodSuffix(billingPeriod: string) {
  const f = formatBillingPeriod(billingPeriod);
  if (f === "1 Month") return "/month";
  if (f === "1 Year") return "/year";
  return `/${f.toLowerCase()}`;
}

/**
 * Parses a billing period string (e.g. "month-1", "year-1") into interval and intervalCount.
 */
export function parseBillingPeriod(billingPeriod: string): {
  interval: string;
  intervalCount: number;
} {
  const [interval = "month", countStr] = billingPeriod.split("-");
  const intervalCount = parseInt(countStr || "1", 10) || 1;
  return { interval, intervalCount };
}

/**
 * Converts a billing period string (e.g. "month-1", "year-1") to a nice name.
 * @param billingPeriod - The billing period string (e.g. "month-1", "year-1")
 * @returns The nice name for the billing period
 */
export function convertBillingPeriodToNiceName(billingPeriod: string) {
  const { interval, intervalCount } = parseBillingPeriod(billingPeriod);
  if (!interval) return "One-time";
  if (interval === "month") {
    return intervalCount === 1 ? "Monthly" : `${intervalCount}-Monthly`;
  }
  if (interval === "year") {
    return intervalCount === 1 ? "Yearly" : `${intervalCount}-Yearly`;
  }
  return `${intervalCount} ${interval}${
    intervalCount && intervalCount > 1 ? "s" : ""
  }`;
}

/**
 * Returns the total number of days in a billing period.
 * @param billingPeriod - The billing period string (e.g. "month-1", "year-1")
 * @returns The total number of days in the billing period
 */
export function getTotalDaysInBillingPeriod(billingPeriod: string) {
  const { interval, intervalCount } = parseBillingPeriod(billingPeriod);
  return interval === "year" ? 365 * intervalCount : 30 * intervalCount;
}

/**
 * Returns the total number of months in a billing period.
 * @param billingPeriod - The billing period string (e.g. "month-1", "year-1")
 * @returns The total number of months in the billing period
 */
export function getTotalMonthsInBillingPeriod(billingPeriod: string) {
  const { interval, intervalCount } = parseBillingPeriod(billingPeriod);
  return interval === "year" ? intervalCount * 12 : intervalCount;
}

/**
 * Calculates the billing anchor date from today based on the billing period.
 * @param billingPeriod - The billing period string (e.g. "month-1", "year-1")
 * @returns The billing anchor date as an ISO string (e.g. "2026-01-20")
 */
export function calculateBillingAnchorFromToday(billingPeriod: string) {
  const totalDays = getTotalDaysInBillingPeriod(billingPeriod);
  const today = new Date();
  const billingAnchor = new Date(
    today.getTime() + totalDays * 24 * 60 * 60 * 1000
  );
  return billingAnchor.toISOString().slice(0, 10);
}

/**
 * Returns the Stripe price for a product that matches the given billing period.
 * billingPeriod format: "interval-intervalCount" (e.g. "month-1", "year-1").
 */
export function getPriceForBillingPeriod(
  prices: CustomStripePrice[],
  currency: string,
  billingPeriod: string
) {
  const { interval, intervalCount } = parseBillingPeriod(billingPeriod);
  const price = prices.find(
    (p) =>
      p.interval === interval &&
      p.intervalCount === intervalCount &&
      p.currency === currency
  );
  if (!price) {
    throw new Error("No price found for billing period");
  }
  return price;
}

/**
 * Returns the subject ids that are entitled to the given price ids.
 * @param stripeProducts - The stripe products
 * @param priceIds - The price ids
 * @returns The subject ids that are entitled to the given price ids
 */
export function getEntitleSubjectIdsFromPriceIds(
  stripeProducts: StripeProductMetadata,
  priceIds: string[]
): Subject["documentId"][] {
  const flatStripeProucts = [
    stripeProducts.allAccessProduct,
    ...stripeProducts.subjectProducts,
  ];
  return priceIds.flatMap((priceId) => {
    const relevantProduct = flatStripeProucts.find((p) =>
      p.prices.some((p) => p.id === priceId)
    );
    if (!relevantProduct) {
      throw new Error("No product found for price id");
    }
    return relevantProduct.includedSubjectIds;
  });
}

/**
 * Builds a billing period string from a Stripe Price's recurring data.
 * Used when deriving billing period from an existing subscription item.
 */
export function billingPeriodFromStripePrice(
  price: Stripe.Price | CustomStripePrice
): string {
  if ("interval" in price) {
    return `${price.interval}-${price.intervalCount}`;
  }
  return `${price.recurring?.interval ?? "month"}-${
    price.recurring?.interval_count ?? 1
  }`;
}

/**
 * Returns the tooltip for the to pay today amount.
 * @param billingPeriod - The billing period string (e.g. "month-1", "year-1")
 * @param billingAnchor - The billing anchor string (e.g. "2026-01-20")
 * @returns The tooltip for the to pay today amount
 */
export function getToPayTodayTooltip(
  billingPeriod: ReturnType<typeof billingPeriodFromStripePrice>,
  billingAnchor: SubscriptionMeta["billingAnchor"]
) {
  return `Your membership renews every ${getTotalDaysInBillingPeriod(
    billingPeriod
  )} days. We charge you for the ${getDaysUntilNextBill(
    billingAnchor
  )} days left before your renewal.`;
}

// ---------------------------------------------------------------------------
// Price / currency amount helpers
// ---------------------------------------------------------------------------

/**
 * Safely gets the unit amount (decimal) for a currency from a price's
 * @param price - Price-like object with optional `unitAmountDecimal` (e.g. StripePrice)
 */
export function getUnitAmountFromPrice(price: CustomStripePrice) {
  return Number(price.unitAmountDecimal);
}

export function buildSelectedSubscriptionMeta(params: {
  accessType: "all-access" | "per-subject" | "";
  allAccessProduct: CustomStripeProduct;
  selectedSubjectProducts: CustomStripeProduct[];
  currency: "gbp" | "usd" | "eur";
  billingPeriod: string;
}): SelectedSubscriptionMeta | undefined {
  const {
    accessType,
    allAccessProduct,
    selectedSubjectProducts,
    currency,
    billingPeriod,
  } = params;
  if (accessType === "all-access") {
    const price = getPriceForBillingPeriod(
      allAccessProduct.prices,
      currency,
      billingPeriod
    );
    return {
      accessType: "all-access",
      currency,
      billingPeriod,
      items: [
        {
          priceId: price.id,
          subjectDocumentIds: allAccessProduct.includedSubjectIds,
          unitAmount: getUnitAmountFromPrice(price),
        },
      ],
    };
  }
  if (accessType === "per-subject" && selectedSubjectProducts.length > 0) {
    return {
      accessType: "per-subject",
      currency,
      billingPeriod,
      items: selectedSubjectProducts.map((product) => {
        const price = getPriceForBillingPeriod(
          product.prices,
          currency,
          billingPeriod
        );
        return {
          priceId: price.id,
          subjectDocumentIds: product.includedSubjectIds,
          unitAmount: getUnitAmountFromPrice(price),
        };
      }),
    };
  }
  return undefined;
}

export interface ChargesTableAmounts {
  toPayToday: string;
  nonDiscountedToPayToday?: string;
  newSubscriptionCost: string;
  nonDiscountedNewSubscriptionCost?: string;
}

/**
 * Computes the four ChargesTable figures from a raw (pre-discount) total,
 * applying the canonical one-time-coupon display rules used across the
 * register form and membership modals.
 *
 * @param rawTotal - pre-discount total in minor units
 * @param discountFraction - 0..1 (e.g. 0.25 for 25% off); 0 = no coupon
 * @param oneTimeCouponUse - true if the coupon only applies to the first charge
 * @param hasTrial - true when nothing is due today (trial in progress)
 */
export function buildChargesTableAmounts(params: {
  rawTotal: number;
  currency: string;
  billingPeriod: string;
  discountFraction?: number;
  oneTimeCouponUse?: boolean;
  hasTrial?: boolean;
}): ChargesTableAmounts {
  const {
    rawTotal,
    currency,
    billingPeriod,
    discountFraction = 0,
    oneTimeCouponUse = false,
    hasTrial = false,
  } = params;

  const hasCoupon = discountFraction > 0;
  const couponSavings = Math.round(rawTotal * discountFraction);
  const totalDue = Math.max(rawTotal - couponSavings, 0);
  const suffix = { suffix: getPricePeriodSuffix(billingPeriod) };

  // Full recurring price only when one-time coupon AND not trialing (coupon already spent today)
  const recurringAmount = oneTimeCouponUse && !hasTrial ? rawTotal : totalDue;
  const showRecurringStrikethrough =
    hasCoupon && !(oneTimeCouponUse && !hasTrial);

  return {
    toPayToday: formatPrice(hasTrial ? 0 : totalDue, currency),
    nonDiscountedToPayToday:
      hasCoupon && !hasTrial ? formatPrice(rawTotal, currency) : undefined,
    newSubscriptionCost: formatPrice(recurringAmount, currency, suffix),
    nonDiscountedNewSubscriptionCost: showRecurringStrikethrough
      ? formatPrice(rawTotal, currency)
      : undefined,
  };
}
